// 아이템 그룹(groupId) 렌더 유닛 계산 — 순수 함수.
// GroupLayer 와 layers.tsx 가 사용한다. 같은 groupId 아이템을 트랙 횡단으로 묶어
// 카드(한 덩어리)를 하나의 변형 단위로 렌더할 수 있게 한다.
import type { EditorStarterItem } from '../../items/item-type';

export interface GroupBBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface GroupUnit {
  groupId: string;
  /** 배경 → 전경 순으로 정렬된 멤버. DOM 순서가 곧 stacking 순서. */
  members: EditorStarterItem[];
  /** 그룹 래퍼의 zIndex(최전경 멤버 기준). */
  zIndex: number;
}

export interface PartitionedGroups {
  groups: GroupUnit[];
  /** 그룹에 속해 GroupLayer 로 렌더되는 아이템 id 집합(per-track 렌더에서 제외용). */
  groupedItemIds: Set<string>;
}

/** 멤버들의 union 경계상자. 빈 입력은 0 경계상자. */
export function computeGroupBBox(
  members: readonly {
    left: number;
    top: number;
    width: number;
    height: number;
  }[],
): GroupBBox {
  if (members.length === 0) return { left: 0, top: 0, width: 0, height: 0 };
  let minL = Infinity;
  let minT = Infinity;
  let maxR = -Infinity;
  let maxB = -Infinity;
  for (const m of members) {
    minL = Math.min(minL, m.left);
    minT = Math.min(minT, m.top);
    maxR = Math.max(maxR, m.left + m.width);
    maxB = Math.max(maxB, m.top + m.height);
  }
  return { left: minL, top: minT, width: maxR - minL, height: maxB - minT };
}

/**
 * 멤버 아이템을 bbox 기준 상대좌표로 변환한다. 원본은 변형하지 않는다(얕은 복제 반환).
 *
 * animations 기본 제거 — groupId 기반 GroupLayer 경로는 "멤버가 공유하는 animations" 를
 * 그룹 애니로 승격해 컨테이너에 1회 적용하므로, 멤버에 그대로 두면 이중 적용된다.
 *
 * keepAnimations: GroupItem(type:'group') 경로 전용. 거기서는 그룹 애니가 group.animations
 * 라는 별도 필드에 있어 멤버 animations 는 순수하게 멤버 개별 애니다 → 이중 적용이 없고,
 * 지우면 오히려 멤버 애니가 조용히 사라진다.
 */
export function toGroupRelativeItem(
  item: EditorStarterItem,
  bbox: GroupBBox,
  opts?: { keepAnimations?: boolean },
): EditorStarterItem {
  const base = {
    ...item,
    left: item.left - bbox.left,
    top: item.top - bbox.top,
  };
  if (!opts?.keepAnimations && 'animations' in base) {
    return { ...base, animations: undefined } as EditorStarterItem;
  }
  return base;
}

/**
 * 보이는 트랙들에서 groupId 를 가진 아이템을 트랙 횡단으로 묶는다.
 * - zIndex 공식은 layers.tsx 와 동일: visibleTracks.length - trackIndex (전경 트랙이 높은 z).
 * - 멤버는 배경→전경(z 오름차순) 정렬 → GroupLayer 가 DOM 순서로 stacking.
 * - 그룹 zIndex = 최전경 멤버 z.
 * - groupId 가 하나도 없으면 빈 결과 → 호출부가 기존 per-track 경로를 그대로 탄다.
 *
 * 주의(미래 오디오 그룹 도입 시 트리거): 입력은 visibleTracks 만이라 hidden 트랙 멤버는
 * groupId 가 있어도 그룹에서 제외된다. 또 GroupLayer 는 audio 를 렌더하지 않는다
 * (현재 그룹 대상은 solid/text 뿐). groupId 를 가진 audio 아이템이 생기면 hidden 트랙
 * 오디오 경로(layers.tsx)와의 이중 렌더를 별도로 처리해야 한다.
 */
export function partitionGroups(
  visibleTracks: readonly { items: string[] }[],
  items: Record<string, EditorStarterItem>,
): PartitionedGroups {
  const n = visibleTracks.length;
  const zById = new Map<string, number>();
  visibleTracks.forEach((track, trackIndex) => {
    const z = n - trackIndex;
    for (const id of track.items) zById.set(id, z);
  });

  const byGroup = new Map<string, { item: EditorStarterItem; z: number }[]>();
  for (const track of visibleTracks) {
    for (const id of track.items) {
      const item = items[id];
      if (item === undefined || item.groupId === undefined) continue;
      const z = zById.get(id) ?? 0;
      const arr = byGroup.get(item.groupId) ?? [];
      arr.push({ item, z });
      byGroup.set(item.groupId, arr);
    }
  }

  const groups: GroupUnit[] = [];
  const groupedItemIds = new Set<string>();
  for (const [groupId, arr] of byGroup) {
    arr.sort((a, b) => a.z - b.z); // 배경 → 전경
    const members = arr.map((x) => x.item);
    const zIndex = Math.max(...arr.map((x) => x.z));
    groups.push({ groupId, members, zIndex });
    for (const m of members) groupedItemIds.add(m.id);
  }

  return { groups, groupedItemIds };
}
